import React, { useState, useRef, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { 
  Card, 
  Input, 
  Button, 
  Typography, 
  Space, 
  Spin, 
  Tag, 
  Divider,
  Empty,
  List,
  Avatar,
  Tooltip,
  Row,
  Col
} from 'antd';
import { 
  SendOutlined, 
  UserOutlined, 
  RobotOutlined, 
  LinkOutlined,
  FileTextOutlined,
  CloseOutlined
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import FileUpload from './FileUpload';
import { sendMessage, ChatMessage, getPrompts, Prompt } from '../services/api';

const { TextArea } = Input;
const { Title, Text, Paragraph } = Typography;

interface ChatInterfaceProps {}

const ChatInterface: React.FC<ChatInterfaceProps> = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<string[]>([]);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [promptsLoading, setPromptsLoading] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const fetchPrompts = async () => {
      try {
        const response = await getPrompts();
        setPrompts(response.prompts);
      } catch (error) {
        console.error('Failed to fetch prompts:', error);
      } finally {
        setPromptsLoading(false);
      }
    };

    fetchPrompts();
  }, []);

  const handleSendMessage = async () => {
    if (!currentMessage.trim() && uploadedFiles.length === 0) return;

    const userMessage: ChatMessage = {
      role: 'user',
      content: currentMessage,
      files: uploadedFiles,
    };

    setMessages(prev => [...prev, userMessage]);
    setCurrentMessage('');
    setUploadedFiles([]);
    setIsLoading(true);

    try {
      const token = await getAccessTokenSilently();
      const response = await sendMessage({
        message: userMessage,
        history: messages,
      }, token);

      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: response.content,
        meta: {
          citations: response.citations,
        },
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage: ChatMessage = {
        role: 'assistant',
        content: 'Sorry, I encountered an error processing your request. Please try again.',
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleFileUpload = (fileId: string) => {
    setUploadedFiles(prev => [...prev, fileId]);
  };

  const removeFile = (fileId: string) => {
    setUploadedFiles(prev => prev.filter(id => id !== fileId));
  };

  const handlePromptClick = (prompt: Prompt) => {
    setCurrentMessage(prompt.title);
  };

  const renderMessage = (message: ChatMessage, index: number) => {
    const isUser = message.role === 'user';
    
    return (
      <div key={index} className={`message-wrapper ${isUser ? 'user' : 'assistant'}`}>
        <Card 
          className={`message-card ${isUser ? 'user-message' : 'assistant-message'}`}
          size="small"
          bordered={false}
        >
          <Space align="start" className="message-content">
            <Avatar
              size={32}
              icon={isUser ? <UserOutlined /> : <RobotOutlined />}
              className={isUser ? 'user-avatar' : 'assistant-avatar'}
            />
            <div className="message-text">
              <ReactMarkdown>{message.content}</ReactMarkdown>
              
              {message.meta?.citations && message.meta.citations.length > 0 && (
                <>
                  <Divider />
                  <div className="citations">
                    <Text strong><LinkOutlined /> References:</Text>
                    <List
                      size="small"
                      dataSource={message.meta.citations}
                      renderItem={(citation: any, citIndex: number) => (
                        <List.Item key={citIndex}>
                          <Space>
                            <FileTextOutlined />
                            <a 
                              href={citation.uri} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="citation-link"
                            >
                              {citation.title || 'Source Document'}
                            </a>
                          </Space>
                        </List.Item>
                      )}
                    />
                  </div>
                </>
              )}
            </div>
          </Space>
        </Card>
      </div>
    );
  };

  return (
    <div className="chat-interface">
      <div className="chat-container">
        <div className="messages-area">
          {messages.length === 0 ? (
            <Card className="welcome-card">
              <Empty
                image={<RobotOutlined style={{ fontSize: 48, color: '#52c41a' }} />}
                description={
                  <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <Title level={4}>Welcome to EUDR Knowledge Agent</Title>
                    <Paragraph type="secondary">
                      I'm here to help you understand the EU Deforestation Regulation. 
                      Ask me anything about compliance requirements, implementation guidelines, 
                      or specific aspects of the regulation.
                    </Paragraph>
                    
                    {promptsLoading ? (
                      <Spin size="small" />
                    ) : prompts.length > 0 && (
                      <div style={{ width: '100%' }}>
                        <Text strong style={{ marginBottom: 16, display: 'block' }}>
                          Quick Start:
                        </Text>
                        <Row gutter={[16, 16]} justify="center">
                          {prompts.map((prompt) => (
                            <Col key={prompt.id} span={24} lg={8}>
                              <Button
                                type="default"
                                size="large"
                                onClick={() => handlePromptClick(prompt)}
                                style={{
                                  width: '100%',
                                  height: 'auto',
                                  minHeight: '80px',
                                  whiteSpace: 'normal',
                                  textAlign: 'left',
                                  padding: '16px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'flex-start',
                                  justifyContent: 'center'
                                }}
                                className="prompt-button"
                              >
                                <Text strong style={{ fontSize: '14px', marginBottom: '4px' }}>
                                  {prompt.title}
                                </Text>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                  {prompt.description}
                                </Text>
                              </Button>
                            </Col>
                          ))}
                        </Row>
                      </div>
                    )}
                  </Space>
                }
              />
            </Card>
          ) : (
            <div className="messages-list">
              {messages.map(renderMessage)}
            </div>
          )}
          
          {isLoading && (
            <div className="message-wrapper assistant">
              <Card className="message-card assistant-message loading" size="small" bordered={false}>
                <Space align="center">
                  <Avatar size={32} icon={<RobotOutlined />} className="assistant-avatar" />
                  <Spin size="small" />
                  <Text type="secondary">Thinking...</Text>
                </Space>
              </Card>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        <Card className="input-card" bordered={false}>
          {uploadedFiles.length > 0 && (
            <div className="uploaded-files">
              <Space wrap>
                {uploadedFiles.map((fileId) => (
                  <Tag
                    key={fileId}
                    closable
                    onClose={() => removeFile(fileId)}
                    icon={<FileTextOutlined />}
                    closeIcon={<CloseOutlined />}
                  >
                    {fileId}
                  </Tag>
                ))}
              </Space>
            </div>
          )}
          
          <div className="input-row">
            <FileUpload onFileUpload={handleFileUpload} />
            
            <TextArea
              value={currentMessage}
              onChange={(e) => setCurrentMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask me about EUDR compliance..."
              autoSize={{ minRows: 1, maxRows: 4 }}
              disabled={isLoading}
              className="message-input"
            />
            
            <Tooltip title="Send message (Enter)">
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={handleSendMessage}
                loading={isLoading}
                disabled={!currentMessage.trim() && uploadedFiles.length === 0}
                className="send-button"
                size="large"
              />
            </Tooltip>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ChatInterface;